
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { History, Calendar, Clock, FileText, Plus, RotateCcw, RefreshCcw } from 'lucide-react';
import { Client } from '@/models/Client';
import { useTranslation } from 'react-i18next';
import { useClientSessionHistory } from '@/hooks/sessions/useClientSessionHistory';
import { useRevertSessionConsumption } from '@/hooks/sessions/useRevertSessionConsumption';
import { useIsMobile, useIsTablet } from '@/hooks/use-mobile';
import SessionHistoryDialog from './SessionHistoryDialog';
import { formatShortDateWithDay } from '@/utils/dateHelpers';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';

interface ClientSessionHistorySectionProps {
  client: Client;
}

const ClientSessionHistorySection: React.FC<ClientSessionHistorySectionProps> = ({ client }) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  
  const { data: sessionHistory = [], isLoading } = useClientSessionHistory({ 
    clientId: client.id, 
    limit: 4 
  });
  
  const revertMutation = useRevertSessionConsumption();

  const handleRevertClick = (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setRevertDialogOpen(true);
  };

  const handleRevertConfirm = () => {
    if (selectedSessionId) {
      revertMutation.mutate({ consumptionId: selectedSessionId });
    }
    setRevertDialogOpen(false);
    setSelectedSessionId(null);
  };

  const getConsumptionTypeBadge = (type: string) => {
    switch (type) {
      case 'completed':
        return <Badge variant="default" className="text-xs">{t('sessions.completed')}</Badge>;
      case 'missed':
        return <Badge variant="destructive" className="text-xs">{t('sessions.missed')}</Badge>;
      default:
        return <Badge variant="secondary" className="text-xs">{type}</Badge>;
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-3 md:pb-6">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-0 sm:items-center sm:justify-between">
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <History className="h-4 w-4 md:h-5 md:w-5" />
              {t('clients.sessionHistory')}
            </CardTitle>
            <Button 
              onClick={() => setShowHistoryDialog(true)}
              variant="outline"
              size="sm"
              className="w-fit text-xs md:text-sm"
            >
              <FileText className="h-3 w-3 md:h-4 md:w-4 mr-2" />
              {t('common.viewAll')}
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          {isLoading ? (
            <div className="text-center text-muted-foreground py-4 text-xs md:text-sm">
              {t('common.loading')}...
            </div>
          ) : sessionHistory.length > 0 ? (
            <div className="space-y-3 md:space-y-4">
              {sessionHistory.map((entry, index) => (
                <div
                  key={entry.consumption_id || `recurring-${entry.consumption_date}-${index}`}
                  className={`flex items-center justify-between p-2 md:p-3 rounded-lg border ${
                    entry.is_cycle_start 
                      ? 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800' 
                      : ''
                  }`}
                >
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-xs md:text-sm">{entry.workout_type_name}</span>
                      {getConsumptionTypeBadge(entry.consumption_type)}
                      {entry.is_cycle_start && (
                        <Badge variant="outline" className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-700">
                          <RefreshCcw className="h-3 w-3 mr-1" />
                          {t('sessions.newCycle')}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-3 md:gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatShortDateWithDay(entry.booking_date, i18n.language)}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {entry.time_slot}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Plus className="h-3 w-3" />
                      <span>{t('common.createdAt')}: {formatShortDateWithDay(entry.created_at, i18n.language)}</span>
                    </div>
                    
                    {entry.notes && (
                      <p className="text-xs text-muted-foreground italic truncate">
                        {entry.notes}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex-shrink-0 ml-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRevertClick(entry.consumption_id)}
                      disabled={revertMutation.isPending}
                      className="h-8 px-2"
                    >
                      <RotateCcw className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-4 text-xs md:text-sm">
              {t('clients.noSessionHistory')}
            </div>
          )}
        </CardContent>
      </Card>

      <SessionHistoryDialog
        open={showHistoryDialog}
        onOpenChange={setShowHistoryDialog}
        clientId={client.id}
        clientName={`${client.firstName} ${client.lastName}`}
      />

      {isMobile || isTablet ? (
        <Sheet open={revertDialogOpen} onOpenChange={setRevertDialogOpen}>
          <SheetContent 
            side="bottom" 
            className={`${isTablet ? 'h-screen w-full rounded-none border-0 p-0 flex flex-col modal-safe-area-aware ios-modal-safe pwa-modal-header-fix' : 'h-[300px] rounded-t-lg'}`}
          >
            <SheetHeader className={`text-left ${isTablet ? 'p-6 border-b flex-shrink-0' : ''}`}>
              <SheetTitle>{t('sessions.confirmRevert')}</SheetTitle>
              <SheetDescription>
                {t('sessions.confirmRevertDescription')}
              </SheetDescription>
            </SheetHeader>
            <div className={`flex flex-col gap-3 ${isTablet ? 'flex-1 p-6 pb-safe-bottom justify-center' : 'mt-6'}`}>
              <Button onClick={handleRevertConfirm} className="w-full">
                {t('sessions.revert')}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setRevertDialogOpen(false)}
                className="w-full"
              >
                {t('common.cancel')}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        <AlertDialog open={revertDialogOpen} onOpenChange={setRevertDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('sessions.confirmRevert')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('sessions.confirmRevertDescription')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleRevertConfirm}>
                {t('sessions.revert')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
};

export default ClientSessionHistorySection;
